// src/components/admin/settings/UsersTab.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  updateDoc,
  doc as fsDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase';

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [allFields, setAllFields] = useState([]); // dynamic field names
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({}); // { uid: { field: newValue, ... } }
  const [processing, setProcessing] = useState(false);
  const [logs, setLogs] = useState([]);

  const FUNCTIONS_BASE = process.env.REACT_APP_FIREBASE_FUNCTIONS;

  // fetch all users and determine union of all field names
  useEffect(() => {
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, 'users'));
      const docs = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      setUsers(docs);
      // collect all keys
      const keys = new Set();
      docs.forEach(u => Object.keys(u).forEach(k => keys.add(k)));
      keys.delete('uid');
      setAllFields(Array.from(keys));
    };
    fetchUsers();
  }, []);

  const log = msg => setLogs(l => [...l, msg]);

  const toggleSelect = uid => {
    setSelected(s => {
      const next = new Set(s);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };
  const selectAll = () =>
    setSelected(new Set(users.map(u => u.uid)));
  const clearAll = () => setSelected(new Set());

  const handleFieldChange = (uid, field, value) => {
    setEdits(e => ({
      ...e,
      [uid]: {
        ...e[uid],
        [field]: value,
      },
    }));
  };

  const saveChanges = async () => {
    if (!Object.keys(edits).length) return;
    setProcessing(true);
    setLogs([]);
    for (const [uid, data] of Object.entries(edits)) {
      try {
        await updateDoc(fsDoc(db, 'users', uid), data);
        log(`Updated user ${uid}`);
      } catch (e) {
        log(`Error updating ${uid}: ${e.message}`);
      }
    }
    setProcessing(false);
    setEdits({});
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    setProcessing(true);
    setLogs([]);
    for (const uid of selected) {
      try {
        // fire a POST to /deleteUser
        const res = await fetch(`${FUNCTIONS_BASE}/deleteUser`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || res.statusText);
        }
        log(`Deleted user ${uid}`);
      } catch (e) {
        log(`Error deleting ${uid}: ${e.message}`);
      }
    }
    // refresh list
    const remaining = users.filter(u => !selected.has(u.uid));
    setUsers(remaining);
    setSelected(new Set());
    setProcessing(false);
  };

  return (
    <div>
      <div className="flex items-center mb-2 space-x-2">
        <button
          onClick={selectAll}
          disabled={processing}
          className="px-3 py-1 rounded bg-[var(--primary-color)] text-white"
        >
          Select All
        </button>
        <button
          onClick={clearAll}
          disabled={processing}
          className="px-3 py-1 rounded bg-[var(--muted-color)] text-white"
        >
          Clear All
        </button>
        <button
          onClick={saveChanges}
          disabled={processing}
          className="px-3 py-1 rounded bg-[var(--card-bg)] text-[var(--text-color)] border border-[var(--muted-color)]"
        >
          {processing ? 'Saving…' : 'Save Changes'}
        </button>
        <button
          onClick={deleteSelected}
          disabled={processing}
          className="px-3 py-1 rounded bg-red-600 text-white"
        >
          {processing ? 'Deleting…' : 'Delete Selected'}
        </button>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="min-w-max table-auto border-collapse whitespace-nowrap">
          <thead>
            <tr>
              <th className="p-2 border">✓</th>
              {allFields.map(f => (
                <th key={f} className="p-2 border text-left">
                  {f}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.uid}>
                <td className="p-2 border">
                  <input
                    type="checkbox"
                    checked={selected.has(user.uid)}
                    onChange={() => toggleSelect(user.uid)}
                  />
                </td>
                {allFields.map(field => (
                  <td key={field} className="p-2 border">
                    <input
                      type="text"
                      className="w-full bg-transparent focus:outline-none"
                      value={
                        edits[user.uid]?.[field] ??
                        user[field] ??
                        ''
                      }
                      onChange={e =>
                        handleFieldChange(
                          user.uid,
                          field,
                          e.target.value
                        )
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="mt-4 max-h-40 overflow-auto p-2 border rounded bg-[var(--card-bg)]"
        style={{ borderColor: 'var(--muted-color)' }}
      >
        {logs.map((l, i) => (
          <div key={i} className="text-sm text-[var(--text-color)]">
            {l}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersTab;
